"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBulkActions = void 0;
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const types_1 = require("@redpanda/types");
const useBulkActions = (config) => {
    const { t: hookT } = (0, react_i18next_1.useTranslation)();
    const { items, idField, entityName, bulkDeleteApi, onSuccess, onOptimisticUpdate, canDeleteItem, userRole, t = hookT, toast: toastFn, pagination, fetchItems, setCurrentPage } = config;
    // 批次操作狀態
    const [selectedItems, setSelectedItems] = (0, react_1.useState)([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = (0, react_1.useState)(false);
    const [isBulkDeleting, setIsBulkDeleting] = (0, react_1.useState)(false);
    // 計算可選擇的項目
    const selectableItems = items.filter(item => canDeleteItem ? canDeleteItem(item) : true);
    const selectableIds = selectableItems.map(item => String(item[idField]));
    // 選擇/取消選擇項目
    const toggleItemSelection = (itemId) => {
        setSelectedItems(prev => prev.includes(itemId)
            ? prev.filter(id => id !== itemId)
            : [...prev, itemId]);
    };
    // 全選/全不選
    const toggleSelectAll = () => {
        if (selectedItems.length === selectableIds.length) {
            setSelectedItems([]);
        }
        else {
            setSelectedItems(selectableIds);
        }
    };
    // 處理批次刪除
    const handleBulkDelete = () => {
        if (selectedItems.length === 0) {
            toastFn?.error(t('common.bulk.noItemsSelected'));
            return;
        }
        setShowBulkDeleteModal(true);
    };
    // 確認批次刪除
    const confirmBulkDelete = async () => {
        if (!bulkDeleteApi) {
            console.error('Bulk delete API not provided');
            return;
        }
        const idsToDelete = [...selectedItems]; // 保存一份副本
        // 只有在沒有樂觀更新時才顯示 loading 狀態
        const hasOptimisticUpdate = !!onOptimisticUpdate;
        if (!hasOptimisticUpdate) {
            setIsBulkDeleting(true);
        }
        // 立即執行樂觀更新
        if (onOptimisticUpdate) {
            onOptimisticUpdate(idsToDelete);
        }
        // 如果有樂觀更新，立即清除選擇和關閉視窗（不等待 API）
        if (hasOptimisticUpdate) {
            setSelectedItems([]);
            setShowBulkDeleteModal(false);
        }
        try {
            const response = await bulkDeleteApi(idsToDelete);
            toastFn?.success(t('common.bulk.bulkDeleteSuccess', { count: idsToDelete.length, entity: entityName }));
            // 如果有樂觀更新函數，說明已經處理了 UI 狀態，不需要重新載入
            if (onOptimisticUpdate) {
                // 只執行 onSuccess 回調，不重新載入數據
                onSuccess?.(idsToDelete, response);
            }
            else {
                // 沒有樂觀更新的情況下，才進行智能分頁處理
                if (pagination && fetchItems) {
                    const currentPage = pagination.currentPage;
                    const totalPages = pagination.totalPages;
                    const itemsOnCurrentPage = items.length;
                    const selectedCount = idsToDelete.length;
                    // 如果刪除後當前頁面會空掉
                    if (itemsOnCurrentPage === selectedCount && totalPages > 1) {
                        if (currentPage > 1) {
                            // 不是第1頁，跳回前一頁
                            const targetPage = Math.min(currentPage - 1, totalPages);
                            fetchItems(targetPage, true);
                        }
                        else {
                            // 第1頁被清空，刷新第1頁讓後面資料補上
                            fetchItems(1, true);
                        }
                    }
                    else {
                        // 否則刷新當前頁面
                        fetchItems(currentPage, true);
                    }
                }
                else {
                    // 使用原有的 onSuccess 回調
                    onSuccess?.(idsToDelete, response);
                }
            }
        }
        catch (error) {
            console.error(`批次刪除${entityName}失敗:`, error);
            // 顯示錯誤訊息（後端統一回 { error }，舊服務是 { message }）
            const serverMessage = error.response?.data?.error || error.response?.data?.message;
            toastFn?.error(serverMessage || t('common.bulk.bulkDeleteFailed', { entity: entityName }));
            // 如果有樂觀更新函數，說明需要手動恢復狀態，而不是重新載入
            if (onOptimisticUpdate) {
                // 呼叫 onSuccess 讓父組件處理錯誤恢復
                onSuccess?.();
            }
            else {
                // 沒有樂觀更新的情況下，重新獲取數據恢復狀態
                if (pagination && fetchItems) {
                    fetchItems(pagination.currentPage, true);
                }
                else {
                    onSuccess?.();
                }
            }
        }
        finally {
            // 只在沒有樂觀更新的情況下才需要清除 loading 和關閉視窗
            if (!hasOptimisticUpdate) {
                setIsBulkDeleting(false);
                setSelectedItems([]);
                setShowBulkDeleteModal(false);
            }
        }
    };
    // 關閉批次刪除模態框
    const closeBulkDeleteModal = () => {
        setShowBulkDeleteModal(false);
    };
    // 檢查項目是否被選中
    const isItemSelected = (itemId) => {
        return selectedItems.includes(itemId);
    };
    // 檢查是否有刪除權限
    const canDeleteItems = () => {
        return (userRole === types_1.Role.SUPER_ADMIN || userRole === types_1.Role.ADMIN);
    };
    // 處理換頁並清除批量選取狀態
    const handlePageChange = (page) => {
        // 換頁前清除批量選取狀態
        setSelectedItems([]);
        // 換頁（store 的 setCurrentPage 本身會載入該頁；沒有時才直接 fetchItems，避免同一頁打兩次 API）
        if (setCurrentPage) {
            setCurrentPage(page);
        }
        else if (fetchItems) {
            fetchItems(page);
        }
    };
    // 計算屬性
    const hasSelectedItems = selectedItems.length > 0;
    const allSelected = selectableIds.length > 0 && selectedItems.length === selectableIds.length;
    return {
        // 狀態
        selectedItems,
        showBulkDeleteModal,
        isBulkDeleting,
        // 操作函數
        toggleItemSelection,
        toggleSelectAll,
        handleBulkDelete,
        confirmBulkDelete,
        closeBulkDeleteModal,
        setSelectedItems,
        isItemSelected,
        handlePageChange,
        // 計算屬性
        hasSelectedItems,
        allSelected,
        canDelete: canDeleteItems,
    };
};
exports.useBulkActions = useBulkActions;
